<script setup>
import { ref, computed, nextTick } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCross from '@/components/icon/IconCross.vue'
import IconPlusTiny from '@/components/icon/IconPlusTiny.vue'
import IconSearch from '@/components/icon/IconSearch.vue'
import IconUpload from '@/components/icon/IconUpload.vue'
import IconDownload from '@/components/icon/IconDownload.vue'
import IconBookOpen from '@/components/icon/IconBookOpen.vue'
import SoundsLikeInput from './SoundsLikeInput.vue'
import {
	VOCAB_MAX,
	VOCAB_MAX_CHARS,
	SEARCH_THRESHOLD,
	SIZE_METER_RATIO,
	newTermRow,
	termKey,
} from './captionRecognition'

// Rows are owned by the parent's draft; text edits mutate them in place and
// structural changes (add, remove, paste) replace the array.
const rows = defineModel('rows', { type: Array, required: true })
const filter = defineModel('filter', { type: String, default: '' })

const props = defineProps({
	// { [rowKey]: { term?, soundsLike? } } blocking errors (client and server).
	errors: { type: Object, default: () => ({}) },
	// { [rowKey]: { index, term } } later copies of an earlier term.
	dupes: { type: Object, default: () => ({}) },
	vocabCount: { type: Number, default: 0 },
	// Characters of the serialized vocab, against VOCAB_MAX_CHARS.
	vocabChars: { type: Number, default: 0 },
	sectionError: { type: String, default: '' },
	disabled: { type: Boolean, default: false },
})

const emit = defineEmits(['import-file', 'export'])

const HELPER = "Names, teams, sponsors, products. One to three words each. Add a 'sounds like' only when the pronunciation isn't what the spelling suggests."

const termInputs = new Map()
const soundInputs = new Map()
const setTermRef = (key) => (el) => { if (el) termInputs.set(key, el); else termInputs.delete(key) }
const setSoundRef = (key) => (el) => { if (el) soundInputs.set(key, el); else soundInputs.delete(key) }
const fileInput = ref(null)
const notice = ref('')

const isBlank = (row) => !row.term.trim() && row.soundsLike.length === 0
const full = computed(() => props.vocabCount >= VOCAB_MAX)
const showSearch = computed(() => props.vocabCount > SEARCH_THRESHOLD || !!filter.value)
const sizeRatio = computed(() => props.vocabChars / VOCAB_MAX_CHARS)
const showMeter = computed(() => sizeRatio.value > SIZE_METER_RATIO)
const formatCount = (n) => n.toLocaleString('en-US')
const meterTone = computed(() => {
	if (sizeRatio.value > 1) return { bar: 'bg-error', text: 'text-error-light' }
	if (sizeRatio.value > 0.9) return { bar: 'bg-gold', text: 'text-gold' }
	return { bar: 'bg-main', text: 'text-surface-7' }
})

const needle = computed(() => termKey(filter.value))
const visible = computed(() => {
	const list = rows.value.map((row, index) => ({ row, index }))
	if (!needle.value) return list
	return list.filter(({ row }) =>
		isBlank(row)
		|| termKey(row.term).includes(needle.value)
		|| row.soundsLike.some((s) => termKey(s).includes(needle.value)))
})
const hiddenCount = computed(() => rows.value.length - visible.value.length)

async function focusRow(key, field = 'term') {
	await nextTick()
	const el = field === 'soundsLike' ? soundInputs.get(key) : termInputs.get(key)
	if (!el) return
	if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center', behavior: 'smooth' })
	el.focus()
}

/**
 * The one way rows get added (Add, Enter, paste): enforces the cap, clears a
 * filter that would hide the new rows, and focuses the last one inserted.
 * `replaceAt` swaps an empty row for the first new one.
 */
function insertRows(at, newRows, { replaceAt = -1, focus = true } = {}) {
	notice.value = ''
	const room = VOCAB_MAX - props.vocabCount
	const blanks = newRows.filter(isBlank)
	const filled = newRows.filter((r) => !isBlank(r))
	const fits = filled.slice(0, Math.max(0, room))
	const dropped = filled.length - fits.length
	const toInsert = [...fits, ...blanks]
	if (!toInsert.length) {
		if (dropped) notice.value = `The dictionary holds up to ${VOCAB_MAX} terms. Remove some to add more.`
		return null
	}
	if (filter.value) filter.value = ''
	const next = [...rows.value]
	if (replaceAt >= 0) next.splice(replaceAt, 1, ...toInsert)
	else next.splice(at, 0, ...toInsert)
	rows.value = next
	if (dropped) notice.value = `Added ${fits.length} ${fits.length === 1 ? 'term' : 'terms'}. ${dropped} didn't fit: the dictionary holds up to ${VOCAB_MAX}.`
	const last = toInsert[toInsert.length - 1]
	if (focus) focusRow(last.key)
	return last.key
}

function addTerm() {
	const trailing = rows.value[rows.value.length - 1]
	if (trailing && isBlank(trailing)) {
		if (filter.value) filter.value = ''
		focusRow(trailing.key)
		return
	}
	if (full.value) {
		notice.value = `The dictionary holds up to ${VOCAB_MAX} terms. Remove one to add another.`
		return
	}
	insertRows(rows.value.length, [newTermRow()])
}

function onTermEnter(index) {
	const row = rows.value[index]
	if (!row || !row.term.trim()) return
	const next = rows.value[index + 1]
	if (next && isBlank(next)) {
		focusRow(next.key)
		return
	}
	if (full.value) {
		notice.value = `The dictionary holds up to ${VOCAB_MAX} terms. Remove one to add another.`
		return
	}
	insertRows(index + 1, [newTermRow()])
}

function onTermBackspace(e, index) {
	const row = rows.value[index]
	if (!row || !isBlank(row) || rows.value.length < 2) return
	e.preventDefault()
	const prev = rows.value[index - 1] || rows.value[index + 1]
	rows.value = rows.value.filter((r) => r.key !== row.key)
	if (prev) focusRow(prev.key)
}

function removeRow(index) {
	const row = rows.value[index]
	if (!row) return
	notice.value = ''
	const neighbour = rows.value[index + 1] || rows.value[index - 1]
	rows.value = rows.value.filter((r) => r.key !== row.key)
	if (neighbour) focusRow(neighbour.key)
}

/** Multi-line text pasted into a term cell becomes one row per line (`Term|pron1,pron2` understood). */
function onTermPaste(e, index) {
	const text = e.clipboardData?.getData('text') || ''
	if (!/[\r\n]/.test(text.trim())) return
	e.preventDefault()
	const parsed = text
		.split(/\r\n|\n|\r/)
		.map((l) => l.trim())
		.filter(Boolean)
		.map((line) => {
			const bar = line.indexOf('|')
			const term = bar >= 0 ? line.slice(0, bar).trim() : line
			const sounds = bar >= 0 ? line.slice(bar + 1).split(',').map((s) => s.trim()).filter(Boolean) : []
			return newTermRow(term, sounds)
		})
	if (!parsed.length) return
	const row = rows.value[index]
	if (row && isBlank(row)) insertRows(index, parsed, { replaceAt: index })
	else insertRows(index + 1, parsed)
}

function pickFile() {
	fileInput.value?.click()
}

async function onFile(e) {
	const file = e.target.files?.[0]
	e.target.value = ''
	if (!file) return
	notice.value = ''
	if (file.size > 1024 * 1024) {
		notice.value = 'That file is too large. Import a plain-text or CSV list under 1 MB.'
		return
	}
	try {
		const text = typeof file.text === 'function' ? await file.text() : await new Response(file).text()
		emit('import-file', { name: file.name, text })
	} catch {
		notice.value = 'Could not read that file. Import a plain-text (.txt) or CSV list.'
	}
}

const errorId = (key) => `vocab-err-${key}`
const rowError = (key) => props.errors[key]?.term || props.errors[key]?.soundsLike || ''
const dupeText = (key) => {
	const d = props.dupes[key]
	if (!d) return ''
	return `Duplicate of “${d.term}” (row ${d.index + 1}). Only the first copy is kept.`
}

defineExpose({ focusRow, addTerm, pickFile })
</script>

<template>
	<div class="flex flex-col gap-3" data-testid="dictionary">
		<input ref="fileInput" type="file" accept=".txt,.csv,text/plain,text/csv" class="hidden" data-testid="dictionary-file" @change="onFile">

		<div class="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
			<div class="flex min-w-0 flex-col gap-1">
				<div class="flex items-baseline gap-2">
					<h3 id="dictionary-heading" class="m-0 text-sm font-semibold text-surface-text-2">Dictionary</h3>
					<span class="text-[11px] tabular-nums" :class="vocabCount > VOCAB_MAX ? 'text-error-light' : 'text-surface-7'" data-testid="vocab-count">{{ vocabCount }} / {{ VOCAB_MAX }}</span>
				</div>
				<p class="m-0 max-w-[62ch] text-xs text-surface-7" data-testid="dictionary-helper">{{ HELPER }}</p>
			</div>
			<div v-if="rows.length" class="flex flex-wrap items-center gap-2">
				<FormButton type="secondary" size="sm" :disabled="disabled" data-testid="import-button" @click="pickFile">
					<icon-base width="14" height="14" class="mr-1.5"><icon-upload /></icon-base>
					Import
				</FormButton>
				<drop-down-menu type="secondary" size="sm" position="right" aria-label="Export dictionary" list-classes="min-w-[200px]" :disabled="!vocabCount">
					<template #toggle-button>
						<icon-base width="14" height="14" class="mr-1.5"><icon-download /></icon-base>
						Export
					</template>
					<template #menu-items>
						<drop-down-menu-item @click="emit('export', 'txt')">Download .txt (Term|sounds like)</drop-down-menu-item>
						<drop-down-menu-item @click="emit('export', 'csv')">Download .csv</drop-down-menu-item>
					</template>
				</drop-down-menu>
			</div>
		</div>

		<!-- Empty: nothing typed yet -->
		<div
			v-if="!rows.length"
			class="flex flex-col items-center gap-3 rounded-lg border border-dashed border-surface-4 px-4 py-8 text-center"
			data-testid="dictionary-empty"
		>
			<span class="flex size-10 items-center justify-center rounded-full bg-surface-3 text-surface-8">
				<icon-base width="18" height="18"><icon-book-open /></icon-base>
			</span>
			<div class="flex flex-col gap-1">
				<p class="m-0 text-sm font-medium text-surface-text-2">Help captions get names right</p>
				<p class="m-0 max-w-[52ch] text-xs text-surface-7">
					Add the names, teams and products your stream mentions so they're spelled correctly every time. Paste a list, or import one from a file.
				</p>
			</div>
			<div class="flex flex-wrap justify-center gap-2">
				<FormButton type="main-outline" size="md" :disabled="disabled" @click="addTerm">
					<icon-base width="10" height="10" width-view-box="12" height-view-box="12" class="mr-1.5"><icon-plus-tiny /></icon-base>
					Add terms
				</FormButton>
				<FormButton type="secondary" size="md" :disabled="disabled" @click="pickFile">
					<icon-base width="14" height="14" class="mr-1.5"><icon-upload /></icon-base>
					Import list
				</FormButton>
			</div>
			<p class="m-0 text-[11px] text-surface-7">Import format: one per line, <code class="text-surface-8">Term</code> or <code class="text-surface-8">Term|pron1,pron2</code></p>
		</div>

		<template v-else>
			<div v-if="showSearch || showMeter" class="flex flex-wrap items-center gap-x-4 gap-y-2">
				<label v-if="showSearch" class="relative m-0 flex w-full items-center sm:w-72">
					<span class="sr-only">Filter terms</span>
					<icon-base width="14" height="14" class="pointer-events-none absolute left-2.5 text-surface-7"><icon-search /></icon-base>
					<input
						v-model="filter"
						type="search"
						class="h-8 w-full rounded border-0 bg-surface-3 pl-8 pr-3 text-base text-surface-text-2 placeholder:text-tiny placeholder:text-surface-7 focus:bg-surface-4 focus:outline-none focus:ring-0 md:text-tiny"
						placeholder="Filter terms or sounds like"
						data-testid="dictionary-filter"
					>
				</label>
				<div v-if="showMeter" class="flex min-w-[180px] flex-1 items-center gap-2 sm:max-w-xs" data-testid="size-meter">
					<div class="h-1 flex-1 overflow-hidden rounded-full bg-surface-3" role="progressbar" :aria-valuenow="Math.round(sizeRatio * 100)" aria-valuemin="0" aria-valuemax="100" aria-label="Dictionary size">
						<div class="h-full transition-all" :class="meterTone.bar" :style="{ width: `${Math.min(100, sizeRatio * 100)}%` }" />
					</div>
					<span class="shrink-0 text-[11px] tabular-nums" :class="meterTone.text">{{ formatCount(vocabChars) }} / {{ formatCount(VOCAB_MAX_CHARS) }} characters</span>
				</div>
			</div>

			<div role="table" aria-labelledby="dictionary-heading" class="flex flex-col gap-2 sm:gap-1.5">
				<div role="row" class="hidden px-0.5 text-[11px] font-medium uppercase tracking-wide text-surface-7 sm:grid sm:grid-cols-[2rem_minmax(0,1fr)_minmax(0,1.4fr)_2rem] sm:gap-2">
					<span role="columnheader" class="text-right">#</span>
					<span role="columnheader">Term</span>
					<span role="columnheader">Sounds like</span>
					<span role="columnheader"><span class="sr-only">Remove</span></span>
				</div>

				<div
					v-for="{ row, index } in visible"
					:key="row.key"
					role="row"
					class="flex flex-col gap-2 rounded-lg bg-surface-3/40 p-3 sm:grid sm:grid-cols-[2rem_minmax(0,1fr)_minmax(0,1.4fr)_2rem] sm:items-start sm:gap-2 sm:rounded-none sm:bg-transparent sm:p-0"
					:data-row="index + 1"
					:data-invalid="rowError(row.key) ? 'true' : undefined"
					:data-duplicate="dupes[row.key] ? 'true' : undefined"
					data-testid="vocab-row"
				>
					<span class="hidden h-8 items-center justify-end text-[11px] tabular-nums text-surface-7 sm:flex" aria-hidden="true">{{ index + 1 }}</span>
					<div role="cell" class="flex min-w-0 flex-col gap-1">
						<label :for="`term-${row.key}`" class="m-0 text-[11px] font-medium text-surface-7 sm:sr-only">Term, row {{ index + 1 }}</label>
						<input
							:id="`term-${row.key}`"
							:ref="setTermRef(row.key)"
							v-model="row.term"
							type="text"
							class="h-8 w-full rounded border-0 bg-surface-3 px-3 text-base text-surface-text-2 placeholder:text-tiny placeholder:text-surface-7 focus:bg-surface-4 focus:outline-none focus:ring-0 md:text-tiny"
							:class="errors[row.key]?.term ? 'ring-1 ring-error-light' : dupes[row.key] ? 'ring-1 ring-gold/60' : ''"
							:placeholder="index === rows.length - 1 ? 'Type a term, press Enter for the next' : 'Term'"
							:aria-invalid="errors[row.key]?.term ? 'true' : undefined"
							:aria-describedby="rowError(row.key) || dupes[row.key] ? errorId(row.key) : undefined"
							:disabled="disabled"
							autocomplete="off"
							spellcheck="false"
							enterkeyhint="next"
							data-testid="term-input"
							@keydown.enter.prevent="onTermEnter(index)"
							@keydown.backspace="onTermBackspace($event, index)"
							@paste="onTermPaste($event, index)"
						>
					</div>
					<div role="cell" class="flex min-w-0 flex-col gap-1">
						<label :for="`sounds-${row.key}`" class="m-0 text-[11px] font-medium text-surface-7 sm:sr-only">Sounds like</label>
						<SoundsLikeInput
							:id="`sounds-${row.key}`"
							:ref="setSoundRef(row.key)"
							v-model="row.soundsLike"
							:term="row.term"
							:invalid="!!errors[row.key]?.soundsLike"
							:described-by="rowError(row.key) ? errorId(row.key) : undefined"
							:disabled="disabled"
							@enter-empty="onTermEnter(index)"
						/>
					</div>
					<div role="cell" class="flex justify-end sm:block">
						<button
							type="button"
							class="inline-flex h-8 items-center gap-1.5 rounded px-2 text-tiny text-surface-7 transition-colors hover:bg-surface-3 hover:text-white disabled:opacity-40 sm:w-8 sm:justify-center sm:px-0"
							:aria-label="row.term.trim() ? `Remove ${row.term.trim()}` : `Remove row ${index + 1}`"
							:disabled="disabled"
							data-testid="remove-term"
							@click="removeRow(index)"
						>
							<icon-base width="10" height="10"><icon-cross /></icon-base>
							<span class="sm:hidden">Remove</span>
						</button>
					</div>
					<p
						v-if="rowError(row.key) || dupes[row.key]"
						:id="errorId(row.key)"
						class="m-0 text-xs sm:col-start-2 sm:col-end-5"
						:class="rowError(row.key) ? 'text-error-light' : 'text-gold'"
						data-testid="row-message"
					>
						{{ rowError(row.key) || dupeText(row.key) }}
					</p>
				</div>

				<p v-if="needle && visible.every(({ row }) => isBlank(row))" class="m-0 py-3 text-center text-xs text-surface-7" data-testid="filter-empty">
					No terms match “{{ filter }}”.
				</p>
			</div>

			<div class="flex flex-wrap items-center gap-3">
				<Tooltip :active="full" content="The dictionary is full. Remove a term to add another." wrapper-class="inline-flex">
					<button
						type="button"
						class="inline-flex h-8 items-center gap-1.5 rounded-full border border-dashed border-surface-5 px-3 text-tiny font-medium text-surface-text-1 transition-colors hover:border-surface-7 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
						:disabled="disabled || full"
						data-testid="add-term"
						@click="addTerm"
					>
						<icon-base width="10" height="10" width-view-box="12" height-view-box="12"><icon-plus-tiny /></icon-base>
						Add term
					</button>
				</Tooltip>
				<span v-if="needle && hiddenCount" class="text-[11px] text-surface-7" data-testid="filter-count">
					Showing {{ visible.length }} of {{ rows.length }}
					<button type="button" class="ml-1 font-medium text-main-light hover:underline" @click="filter = ''">Clear filter</button>
				</span>
				<span class="hidden text-[11px] text-surface-7 lg:inline">Tip: paste a list into a term to add one row per line.</span>
			</div>
		</template>

		<p v-if="sectionError" class="m-0 text-xs text-error-light" data-testid="vocab-section-error">{{ sectionError }}</p>
		<p v-if="notice" class="m-0 text-xs text-gold" role="status" data-testid="dictionary-notice">{{ notice }}</p>
	</div>
</template>
